import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fileUrl } from "@/lib/api";
import { rupiah, formatDate, angka } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";

export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const [rate, setRate] = useState(null);
  const [att, setAtt] = useState([]);
  const [kasbon, setKasbon] = useState([]);
  const [allow, setAllow] = useState([]);
  const [ded, setDed] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/employees/${id}`).then((r) => setEmp(r.data));
    api.get(`/salary-rates/active/${id}`).then((r) => setRate(r.data));
    api.get(`/attendance?employee_id=${id}`).then((r) => setAtt(r.data));
    api.get(`/cash-advances?employee_id=${id}`).then((r) => setKasbon(r.data));
    api.get(`/employee-allowances?employee_id=${id}`).then((r) => setAllow(r.data));
    api.get(`/deductions?employee_id=${id}`).then((r) => setDed(r.data));
    api.get(`/employees/${id}/history`).then((r) => setHistory(r.data)).catch(() => {});
  }, [id]);

  if (!emp) return <div className="text-slate-400">Memuat...</div>;

  const totalLembur = att.reduce((s, a) => s + Number(a.jam_lembur || 0), 0);
  const hariMasuk = att.filter((a) => a.status === "Hadir").length;
  const totalKasbon = kasbon.reduce((s, k) => s + Number(k.nominal || 0), 0);
  const sisaKasbon = kasbon.reduce((s, k) => s + Number(k.sisa || 0), 0);

  const Stat = ({ label, value }) => (
    <Card className="p-4"><div className="text-xs text-slate-500">{label}</div><div className="text-xl font-black font-mono text-slate-900 mt-1">{value}</div></Card>
  );

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => nav("/employees")} className="w-fit -ml-2"><ArrowLeft className="h-4 w-4 mr-1" /> Kembali</Button>
      <PageHeader title={emp.nama} subtitle={`${emp.kode_pegawai} · ${emp.jabatan || "-"} · ${emp.status_pegawai}`}>
        <StatusBadge status={emp.aktif ? "Aktif" : "Nonaktif"} />
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Hari Masuk" value={`${hariMasuk} hari`} />
        <Stat label="Total Jam Lembur" value={`${totalLembur} jam`} />
        <Stat label="Total Kasbon" value={rupiah(totalKasbon)} />
        <Stat label="Sisa Kasbon" value={rupiah(sisaKasbon)} />
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="gaji" data-testid="tab-gaji">Gaji</TabsTrigger>
          <TabsTrigger value="absensi" data-testid="tab-absensi">Absensi & Lembur</TabsTrigger>
          <TabsTrigger value="kasbon" data-testid="tab-kasbon">Kasbon</TabsTrigger>
          <TabsTrigger value="tunjangan" data-testid="tab-tunjangan">Tunjangan</TabsTrigger>
          <TabsTrigger value="potongan" data-testid="tab-potongan">Potongan</TabsTrigger>
          <TabsTrigger value="riwayat" data-testid="tab-riwayat">Riwayat Transfer</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card className="p-5 space-y-4">
            {(emp.foto_url || emp.foto_ktp_url) && (
              <div className="flex flex-wrap gap-4">
                {emp.foto_url && <div><div className="text-xs text-slate-500 mb-1">Foto Pegawai</div><img src={fileUrl(emp.foto_url)} alt="foto" className="h-28 w-28 rounded-lg object-cover border" data-testid="det-foto" /></div>}
                {emp.foto_ktp_url && <div><div className="text-xs text-slate-500 mb-1">Foto KTP</div><img src={fileUrl(emp.foto_ktp_url)} alt="ktp" className="h-28 w-44 rounded-lg object-cover border" data-testid="det-ktp" /></div>}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
            {[["NIK/KTP", emp.nik], ["Nama Panggilan", emp.nama_panggilan], ["Skill", emp.skill],
              ["No. HP", emp.no_hp], ["Alamat", emp.alamat], ["Mulai Kerja", formatDate(emp.tanggal_mulai_kerja)],
              ["Lokasi", emp.lokasi], ["Bank", emp.nama_bank], ["No. Rekening", emp.no_rekening],
              ["Pemilik Rekening", emp.nama_pemilik_rekening], ["Catatan", emp.catatan]].map(([l, v]) => (
              <div key={l} className="flex justify-between border-b border-slate-100 pb-2">
                <span className="text-slate-500">{l}</span><span className="font-medium text-slate-800">{v || "-"}</span>
              </div>
            ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="gaji">
          <Card className="p-5">
            {rate ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                {[["Gaji/Hari", rate.gaji_per_hari], ["Uang Makan/Hari", rate.uang_makan_per_hari],
                  ["Luar Kota/Hari", rate.uang_luar_kota_per_hari], ["Lembur/Jam", rate.tarif_lembur_per_jam],
                  ["Tj Jabatan", rate.tunjangan_jabatan], ["Tj Skill", rate.tunjangan_skill],
                  ["Tj Tetap", rate.tunjangan_tetap]].map(([l, v]) => (
                  <div key={l}><div className="text-xs text-slate-500">{l}</div><div className="font-mono font-semibold">{rupiah(v)}</div></div>
                ))}
              </div>
            ) : <p className="text-slate-400">Belum ada tarif gaji. Set di menu Master Gaji.</p>}
          </Card>
        </TabsContent>

        <TabsContent value="absensi">
          <DataTable testid="det-att" data={att} columns={[
            { key: "tanggal", label: "Tanggal", sortable: true, render: (r) => formatDate(r.tanggal) },
            { key: "status", label: "Status" },
            { key: "jam_masuk", label: "Masuk" }, { key: "jam_pulang", label: "Pulang" },
            { key: "jam_lembur", label: "Lembur (jam)", align: "right" },
            { key: "keterangan", label: "Keterangan" },
          ]} />
        </TabsContent>

        <TabsContent value="kasbon">
          <DataTable testid="det-kasbon" data={kasbon} columns={[
            { key: "nomor", label: "No" }, { key: "tanggal", label: "Tanggal", render: (r) => formatDate(r.tanggal) },
            { key: "nominal", label: "Nominal", align: "right", render: (r) => rupiah(r.nominal) },
            { key: "terpotong", label: "Terpotong", align: "right", render: (r) => rupiah(r.terpotong) },
            { key: "sisa", label: "Sisa", align: "right", render: (r) => rupiah(r.sisa) },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} />
        </TabsContent>

        <TabsContent value="tunjangan">
          <DataTable testid="det-allow" data={allow} columns={[
            { key: "nama", label: "Nama" }, { key: "nominal", label: "Nominal", align: "right", render: (r) => rupiah(r.nominal) },
            { key: "keterangan", label: "Keterangan" },
          ]} />
        </TabsContent>

        <TabsContent value="potongan">
          <DataTable testid="det-ded" data={ded} columns={[
            { key: "jenis", label: "Jenis" }, { key: "tanggal", label: "Tanggal", render: (r) => formatDate(r.tanggal) },
            { key: "nominal", label: "Nominal", align: "right", render: (r) => rupiah(r.nominal) },
            { key: "keterangan", label: "Keterangan" },
          ]} />
        </TabsContent>

        <TabsContent value="riwayat">
          <DataTable testid="det-history" data={history} columns={[
            { key: "tanggal", label: "Tanggal", sortable: true, render: (r) => formatDate(r.tanggal) },
            { key: "project_nama", label: "Proyek" },
            { key: "pimpinan_nama", label: "Pimpinan/Mandor" },
            { key: "lokasi", label: "Lokasi" },
            { key: "catatan", label: "Catatan" },
          ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
